// Settings → Profile avatar uploader. Three-step flow per file:
//   1. POST /settings/profile/avatar/sign     → presigned PUT URL
//   2. PUT bytes directly to the presigned URL
//   3. POST /settings/profile/avatar/commit   → returns the replacement
//      <div id="profile-avatar"> for in-place swap.
// "Remove photo" hits DELETE /settings/profile/avatar and swaps in the
// initials-bubble fallback the server returns.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-profile-avatar]";
const FALLBACK_UPLOAD_ERROR = "Could not upload your photo. Please try again.";
const FALLBACK_REMOVE_ERROR = "Could not remove your photo. Please try again.";

export function initProfileAvatar(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireWidget);
}

function wireWidget(widget) {
  if (!firstBind(widget)) return;

  const input = widget.querySelector("[data-profile-avatar-input]");
  const removeBtn = widget.querySelector("[data-profile-avatar-remove]");
  const errorEl = widget.querySelector("[data-profile-avatar-error]");
  const statusEl = widget.querySelector("[data-profile-avatar-status]");

  if (input) {
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) return;

      hide(errorEl);
      setStatus(statusEl, "Uploading…");
      input.disabled = true;

      try {
        const fragmentHTML = await uploadOne(file);
        replaceAvatar(widget, fragmentHTML);
      } catch (err) {
        showError(errorEl, errorMessage(err, FALLBACK_UPLOAD_ERROR));
      } finally {
        hide(statusEl);
        input.disabled = false;
        input.value = "";
      }
    });
  }

  if (removeBtn) {
    removeBtn.addEventListener("click", async () => {
      hide(errorEl);
      removeBtn.disabled = true;
      setStatus(statusEl, "Removing…");

      try {
        const resp = await fetch("/settings/profile/avatar", {
          method: "DELETE",
          credentials: "same-origin",
          headers: { Accept: "text/html" },
        });
        if (!resp.ok) throw await asUserError(resp);

        const fragmentHTML = await resp.text();
        replaceAvatar(widget, fragmentHTML);
      } catch (err) {
        removeBtn.disabled = false;
        showError(errorEl, errorMessage(err, FALLBACK_REMOVE_ERROR));
      } finally {
        hide(statusEl);
      }
    });
  }
}

async function uploadOne(file) {
  const signParams = new URLSearchParams();
  signParams.append("filename", file.name);
  signParams.append("content_type", file.type || "application/octet-stream");
  signParams.append("file_size_bytes", String(file.size));

  const signResp = await fetch("/settings/profile/avatar/sign", {
    method: "POST",
    body: signParams,
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!signResp.ok) throw await asUserError(signResp);

  const signed = await signResp.json();
  if (!signed.upload_url || !signed.key) {
    throw new Error(FALLBACK_UPLOAD_ERROR);
  }

  // upload_headers are the signed metadata headers from the sign step;
  // S3 rejects the PUT signature unless they're echoed back verbatim.
  const putResp = await fetch(signed.upload_url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...(signed.upload_headers || {}),
    },
  });
  if (!putResp.ok) throw new Error(FALLBACK_UPLOAD_ERROR);

  const commitParams = new URLSearchParams();
  commitParams.append("key", signed.key);

  const commitResp = await fetch("/settings/profile/avatar/commit", {
    method: "POST",
    body: commitParams,
    headers: { Accept: "text/html" },
    credentials: "same-origin",
  });
  if (!commitResp.ok) throw await asUserError(commitResp);

  return commitResp.text();
}

function replaceAvatar(widget, fragmentHTML) {
  const avatar = widget.querySelector("#profile-avatar");
  if (!avatar) return;
  avatar.outerHTML = fragmentHTML;
}

async function asUserError(resp) {
  try {
    const body = await resp.json();
    if (body && typeof body.error === "string") {
      return new Error(body.error);
    }
  } catch (_) {
    // not JSON — fall through to status text
  }
  return new Error(`Request failed (${resp.status})`);
}

function errorMessage(err, fallback) {
  if (err && err.message) return err.message;
  return fallback;
}

function showError(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}

function hide(el) {
  if (!el) return;
  el.textContent = "";
  el.classList.add("hidden");
}

function setStatus(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}
